import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata'

// Enums matching backend
export type FeatureRequestCategory =
  | 'UiUx'
  | 'WordAddin'
  | 'ExcelAddin'
  | 'PowerPointAddin'
  | 'OutlookAddin'
  | 'Api'
  | 'Integrations'
  | 'AiAutomation'
  | 'Workflows'
  | 'Templates'
  | 'Other'

export type FeatureRequestStatus =
  | 'Submitted'
  | 'UnderReview'
  | 'Planned'
  | 'InProgress'
  | 'Completed'
  | 'Declined'

// Request DTOs
export interface SubmitFeatureRequestRequest {
  title: string
  description: string
  category: FeatureRequestCategory
}

export interface UpdateFeatureStatusRequest {
  status: FeatureRequestStatus
  adminResponse?: string
}

// Response DTOs
export interface FeatureRequestDto {
  id: string
  title: string
  description: string
  category: FeatureRequestCategory
  status: FeatureRequestStatus
  submittedByUserId?: string
  submittedByEmail?: string
  submittedByName?: string
  adminResponse?: string
  respondedAt?: string
  voteCount: number
  hasVoted?: boolean
  createdAt: string
  updatedAt: string
}

export interface FeatureSubmitResponse {
  success: boolean
  message: string
  featureRequestId: string
}

export interface VoteResultDto {
  success: boolean
  voted: boolean
  newVoteCount: number
}

export interface UserVoteStatusDto {
  hasVoted: boolean
}

export interface TopVotedFeatureDto {
  id: string
  title: string
  voteCount: number
  category: FeatureRequestCategory
  status: FeatureRequestStatus
}

export interface FeatureStatsDto {
  totalRequests: number
  submittedCount: number
  underReviewCount: number
  plannedCount: number
  inProgressCount: number
  completedCount: number
  declinedCount: number
  totalVotes: number
  requestsByCategory: Record<string, number>
  topVoted: TopVotedFeatureDto[]
}

export interface FeatureRequestsODataResponse {
  value: FeatureRequestDto[]
  '@odata.count'?: number
}

/**
 * Normalizes an object's keys from PascalCase to camelCase.
 */
function normalizeKeys<T>(obj: Record<string, unknown>): T {
  const result: Record<string, unknown> = {}
  for (const key in obj) {
    const camelKey = key.charAt(0).toLowerCase() + key.slice(1)
    const value = obj[key]
    if (Array.isArray(value)) {
      result[camelKey] = value.map(item =>
        typeof item === 'object' && item !== null
          ? normalizeKeys(item as Record<string, unknown>)
          : item
      )
    } else if (typeof value === 'object' && value !== null) {
      result[camelKey] = normalizeKeys(value as Record<string, unknown>)
    } else {
      result[camelKey] = value
    }
  }
  return result as T
}

export const featureRequestService = {
  /**
   * Get public feature requests with OData query support.
   */
  async getFeatureRequests(params?: ODataQueryParams): Promise<FeatureRequestsODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<FeatureRequestsODataResponse | FeatureRequestDto[]>(`/v1/features${queryString}`)

      // Handle both OData response and array response for backwards compatibility
      if (Array.isArray(response.data)) {
        return {
          value: response.data.map(item => normalizeKeys<FeatureRequestDto>(item as unknown as Record<string, unknown>)),
          '@odata.count': response.data.length
        }
      }

      // Normalize OData response - keys are PascalCase from OData
      const data = response.data as { value?: unknown[]; '@odata.count'?: number }
      return {
        value: (data.value || []).map(item => normalizeKeys<FeatureRequestDto>(item as unknown as Record<string, unknown>)),
        '@odata.count': data['@odata.count']
      }
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get a single feature request by ID.
   */
  async getFeatureRequest(id: string): Promise<FeatureRequestDto> {
    try {
      const response = await api.get<FeatureRequestDto>(`/v1/features/${id}`)
      return normalizeKeys<FeatureRequestDto>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Submit a new feature request (requires authentication).
   */
  async submitFeatureRequest(data: SubmitFeatureRequestRequest): Promise<FeatureSubmitResponse> {
    try {
      const response = await api.post<FeatureSubmitResponse>('/v1/features', data)
      return normalizeKeys<FeatureSubmitResponse>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Toggle vote on a feature request (requires authentication).
   */
  async toggleVote(id: string): Promise<VoteResultDto> {
    try {
      const response = await api.post<VoteResultDto>(`/v1/features/${id}/vote`)
      return normalizeKeys<VoteResultDto>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Check if current user has voted on a feature request.
   */
  async hasVoted(id: string): Promise<boolean> {
    try {
      const response = await api.get<UserVoteStatusDto>(`/v1/features/${id}/voted`)
      const data = normalizeKeys<UserVoteStatusDto>(response.data as unknown as Record<string, unknown>)
      return data.hasVoted
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get all feature requests for admin (includes declined).
   */
  async getAdminFeatureRequests(params?: ODataQueryParams): Promise<FeatureRequestsODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<FeatureRequestsODataResponse | FeatureRequestDto[]>(`/v1/features/admin${queryString}`)

      // Handle both OData response and array response for backwards compatibility
      if (Array.isArray(response.data)) {
        return {
          value: response.data.map(item => normalizeKeys<FeatureRequestDto>(item as unknown as Record<string, unknown>)),
          '@odata.count': response.data.length
        }
      }

      // Normalize OData response - keys are PascalCase from OData
      const data = response.data as { value?: unknown[]; '@odata.count'?: number }
      return {
        value: (data.value || []).map(item => normalizeKeys<FeatureRequestDto>(item as unknown as Record<string, unknown>)),
        '@odata.count': data['@odata.count']
      }
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Update feature request status (admin only).
   */
  async updateStatus(id: string, data: UpdateFeatureStatusRequest): Promise<FeatureRequestDto> {
    try {
      const response = await api.put<FeatureRequestDto>(`/v1/features/${id}/status`, data)
      return normalizeKeys<FeatureRequestDto>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Delete a feature request (admin only).
   */
  async deleteFeatureRequest(id: string): Promise<void> {
    try {
      await api.delete(`/v1/features/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get feature request statistics (admin only).
   */
  async getStats(): Promise<FeatureStatsDto> {
    try {
      const response = await api.get<FeatureStatsDto>('/v1/features/stats')
      return normalizeKeys<FeatureStatsDto>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}

// Category display names for UI
export const categoryLabels: Record<FeatureRequestCategory, string> = {
  UiUx: 'UI/UX',
  WordAddin: 'Word Add-in',
  ExcelAddin: 'Excel Add-in',
  PowerPointAddin: 'PowerPoint Add-in',
  OutlookAddin: 'Outlook Add-in',
  Api: 'API',
  Integrations: 'Integrations',
  AiAutomation: 'AI & Automation',
  Workflows: 'Workflows',
  Templates: 'Templates',
  Other: 'Other',
}

// Status display names and colors for UI
export const statusConfig: Record<FeatureRequestStatus, { label: string; color: string }> = {
  Submitted: { label: 'Submitted', color: 'gray' },
  UnderReview: { label: 'Under Review', color: 'blue' },
  Planned: { label: 'Planned', color: 'purple' },
  InProgress: { label: 'In Progress', color: 'yellow' },
  Completed: { label: 'Completed', color: 'green' },
  Declined: { label: 'Declined', color: 'red' },
}
